import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, CheckCircle, XCircle } from "lucide-react";
import QuotationDrawer from "./QuotationDrawer";

// Helper function to format the date from the API response
const formatDate = (isoDateString) => {
  if (!isoDateString) return "";
  try {
    const date = new Date(isoDateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    console.error("Error formatting date:", e);
    return "";
  }
};

const SampleIssueTab = ({ BASE_URL, COMPANYCODE, DEPARTMENT }) => {
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotationDrawerOpen, setQuotationDrawerOpen] = useState(false);

  // Fetch data from the API
  const fetchApprovedRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const apiEndpoint = `${BASE_URL}/api/CRMTaskMainListFilter/${COMPANYCODE}/SAMPLEISSUEMASTER/-/-/-/-/${DEPARTMENT}/1900-01-01/1900-01-01/-/-/-`;

    try {
      const response = await fetch(apiEndpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setApprovedRequests(data);
    } catch (e) {
      console.error("Could not fetch approved requests:", e);
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovedRequests();
  }, [fetchApprovedRequests]);

  // Single select - clicking a row selects/deselects it
  const handleRowClick = (proposalNo) => {
    setSelectedRequest(selectedRequest === proposalNo ? null : proposalNo);
    // if (selectedRequest) {
    setQuotationDrawerOpen(true);
    // }
  };

  const handleRefresh = () => {
    setSelectedRequest(null); // Clear selection
    fetchApprovedRequests();
  };

  // Open quotation drawer
  const handleOpenQuotations = () => {
    if (selectedRequest) {
      setQuotationDrawerOpen(true);
    }
  };

  const getSelectedRequestData = () => {
    return approvedRequests.find((req) => req.Proposal_No === selectedRequest);
  };

  return (
    <div className="rounded-xl overflow-hidden flex flex-col p-3">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-2 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800">
            Sample Issue - Approved Requests
            <span className="text-blue-600 ml-1">
              ({approvedRequests?.length ?? 0})
            </span>
          </h2>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className={`p-2 rounded-full transition-colors ${
              isLoading
                ? "text-gray-400 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            title="Refresh Data"
          >
            <RefreshCw
              className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenQuotations}
            disabled={!selectedRequest}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-semibold transition-all ${
              selectedRequest
                ? "bg-cyan-600 hover:bg-cyan-700 text-white shadow-sm"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            View Items
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-x-auto overflow-y-auto border border-gray-200 rounded-lg mt-4 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mr-2" />
            <p className="text-blue-500 text-sm">
              Loading approved requests...
            </p>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16">
            <XCircle className="w-6 h-6 text-red-500 mr-2" />
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        ) : (
          <div className="min-w-max">
            {/* Table Header */}
            <div className="grid grid-cols-[120px_120px_0.6fr_150px_150px_150px_150px] gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Proposal No
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Date
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Customer
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Customer Acc
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Salesperson ID
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                Salesperson Acc
              </div>

              <div className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider text-center">
                Total Sample Qty
              </div>
            </div>

            {/* Table Body */}
            <div>
              {approvedRequests?.length > 0 ? (
                approvedRequests.map((row, index) => (
                  <div
                    key={`${row.Proposal_No}-${index}`}
                    onClick={() => handleRowClick(row.Proposal_No)}
                    className={`grid grid-cols-[120px_120px_0.6fr_150px_150px_150px_150px] gap-3 px-4 py-3 items-center hover:bg-slate-50/50 transition-all cursor-pointer border-l-2 ${
                      selectedRequest === row.Proposal_No
                        ? "bg-cyan-50 border-l-cyan-500 shadow-sm"
                        : "border-l-transparent hover:border-l-cyan-300"
                    }`}
                  >
                    {/* Proposal No */}
                    <div className="text-sm font-bold text-slate-700">
                      {row.Proposal_No}
                    </div>

                    {/* Date */}
                    <div className="text-sm text-slate-500 whitespace-nowrap">
                      {row.Date ? formatDate(row.Date) : ""}
                    </div>

                    {/* Customer */}
                    <div
                      className="text-sm text-slate-700 font-medium truncate"
                      title={row.Customer}
                    >
                      {row.Customer}
                    </div>

                    {/* Customer Account */}
                    <div className="text-sm text-slate-600">{row.Cust_acc}</div>

                    {/* Salesperson ID */}
                    <div className="text-sm text-slate-600">
                      {row.Salesperson}
                    </div>

                    {/* Salesperson Name */}
                    <div className="text-sm text-slate-600">
                      {row.Salesperson_Acc}
                    </div>

                    {/* Total Sample Qty */}
                    <div className="text-sm text-slate-800 font-semibold text-center">
                      {row.Total_Sample_Qty}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center py-16">
                  <p className="text-gray-400 text-sm">
                    No approved requests found
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quotation Drawer */}
      <QuotationDrawer
        drawerOpen={quotationDrawerOpen}
        handleCloseDrawer={() => setQuotationDrawerOpen(false)}
        proposalNumber={selectedRequest}
        BASE_URL={BASE_URL}
        COMPANY_CODE={COMPANYCODE}
        DEPARTMENT={DEPARTMENT}
        sampleIssueData={getSelectedRequestData()}
      />
    </div>
  );
};

export default SampleIssueTab;
